export interface PackageLike {
  id: string; name: string; basePrice?: number; features?: string[]; status?: string;
  familyId?: string; tierRank?: number; role?: string; tierLabel?: string; [k: string]: unknown;
}
export interface FamilyLike { id: string; tierIds: string[]; topTierId?: string; defaultTierId?: string; name?: string }
export interface TierDraft { rank: 1 | 2 | 3; id?: string; label: string; priceDollars: string; sell: boolean; features: string[] }
export type TierSaveOp =
  | { kind: 'createPackage'; body: Record<string, unknown> }
  | { kind: 'updatePackage'; id: string; body: Record<string, unknown> }
  | { kind: 'createFamily'; body: Record<string, unknown> }
  | { kind: 'updateFamily'; id: string; body: Record<string, unknown> };

const LABELS = ['Basic', 'Pro', 'Advanced'] as const;
const dollars = (cents?: number) => ((cents || 0) / 100).toFixed(2);
const cents = (d: string) => Math.round(parseFloat(d || '0') * 100);
const sameFeatures = (a: string[] = [], b: string[] = []) =>
  a.length === b.length && [...a].sort().every((v, i) => v === [...b].sort()[i]);
const sameIds = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);

function topOf(f: FamilyLike): string | undefined { return f.topTierId ?? f.tierIds[f.tierIds.length - 1]; }

/**
 * The family a package belongs to, or undefined. A package's own row may not
 * carry `familyId` yet in-memory (e.g. right after another tier's save
 * refetched packages but not this one) — a family that already lists this
 * package's id by position is the same family, so fall back to that before
 * giving up. The ONE lookup both `tiersForPackage` and the editor's submit
 * handler use, so they can never disagree about which family a package is in.
 */
export function familyOf(pkg: PackageLike, families: FamilyLike[]): FamilyLike | undefined {
  return families.find((f) => f.id === pkg.familyId || f.tierIds.includes(pkg.id));
}

export function visiblePackages<T extends PackageLike>(pkgs: T[], families: FamilyLike[]): T[] {
  return pkgs.filter((p) => {
    if (p.role !== 'tier' || !p.familyId) return true;
    const f = families.find((x) => x.id === p.familyId);
    return !f || topOf(f) === p.id;
  });
}

export function tiersForPackage(pkg: PackageLike, all: PackageLike[], families: FamilyLike[]): TierDraft[] {
  const fam = familyOf(pkg, families);
  return ([1, 2, 3] as const).map((rank) => {
    if (rank === 3) {
      return { rank, id: pkg.id, label: pkg.tierLabel || LABELS[2], priceDollars: dollars(pkg.basePrice), sell: true, features: [...(pkg.features || [])] };
    }
    // A sibling is matched either by the family's own tierIds list (the
    // normal case) OR by familyId+tierRank alone (IMPORTANT 2: a retry after
    // a partial failure can leave a tier package CREATED but not yet linked
    // into the family's tierIds — an orphan. Matching it here means a retry
    // updates that row instead of creating a duplicate.)
    const sib = fam ? all.find((p) => p.tierRank === rank && (fam.tierIds.includes(p.id) || p.familyId === fam.id)) : undefined;
    // Family names an id with no package row yet (Ruby AI): reuse it, by position.
    const reservedId = fam && !sib && fam.tierIds.length === 3 && !all.some((p) => p.id === fam.tierIds[rank - 1]) ? fam.tierIds[rank - 1] : undefined;
    return sib
      ? { rank, id: sib.id, label: sib.tierLabel || LABELS[rank - 1], priceDollars: dollars(sib.basePrice), sell: sib.status === 'active', features: [...(sib.features || [])] }
      : { rank, id: reservedId, label: LABELS[rank - 1], priceDollars: '0.00', sell: false, features: [] };
  });
}

export function planTierSave(
  pkg: PackageLike, drafts: TierDraft[], family: FamilyLike | null, all: PackageLike[],
): TierSaveOp[] {
  const lower = drafts.filter((d) => d.rank < 3);
  // A package that has never had tiers and gets none stays EXACTLY as today.
  if (!family && !lower.some((d) => d.sell)) return [];
  const familyId = family?.id ?? pkg.familyId ?? `fam_${pkg.id.toLowerCase().replace(/[^a-z0-9_]/g, '_')}`.slice(0, 64);
  const ops: TierSaveOp[] = [];
  const tierIds: string[] = [];
  for (const d of lower) {
    const row = d.id ? all.find((p) => p.id === d.id) : undefined;
    const body = {
      name: pkg.name, description: (pkg.description as string) || pkg.name,
      familyId, tierRank: d.rank, role: 'tier', tierLabel: d.label,
      basePrice: cents(d.priceDollars), features: d.features,
      status: d.sell ? 'active' : 'deprecated',
      productLine: pkg.productLine, icon: pkg.icon, colorType: pkg.colorType, color: pkg.color,
      gradientFrom: pkg.gradientFrom, gradientTo: pkg.gradientTo, menuGroupId: pkg.menuGroupId,
    };
    if (row) {
      // The row already exists — update it ONLY when something about it
      // actually changed. An untouched tier (including one already OFF)
      // must never re-emit a write, and must keep its id in the family.
      const changed = (row.tierLabel || LABELS[d.rank - 1]) !== d.label
        || (row.basePrice || 0) !== cents(d.priceDollars)
        || (row.status === 'active') !== d.sell
        || !sameFeatures(row.features, d.features);
      if (changed) ops.push({ kind: 'updatePackage', id: row.id, body });
      tierIds.push(row.id);
    } else if (d.sell) {
      ops.push({ kind: 'createPackage', body: { ...(d.id ? { id: d.id } : {}), ...body } });
      tierIds.push(d.id ?? `@new${d.rank}`);
    } else if (d.id) {
      // CRITICAL 1: a reserved id (e.g. Ruby AI's pkg_ai_default) with no row
      // yet and still not sold — untouched. Keep its place in the family so
      // it is never dropped; emit no op for it.
      tierIds.push(d.id);
    }
    // else: never had a slot and still isn't sold — nothing to add.
  }
  const top = drafts[2];
  const topLabel = pkg.tierLabel || LABELS[2];
  const topChanged = pkg.familyId !== familyId || pkg.tierRank !== 3 || pkg.role !== 'tier' || topLabel !== top.label;
  if (topChanged) {
    ops.push({ kind: 'updatePackage', id: pkg.id, body: { familyId, tierRank: 3, role: 'tier', tierLabel: top.label } });
  }
  tierIds.push(pkg.id);
  const defaultTierId = family?.defaultTierId && tierIds.includes(family.defaultTierId) ? family.defaultTierId : pkg.id;
  const familyBody = { id: familyId, name: pkg.name, tierIds, topTierId: pkg.id, defaultTierId };
  if (family) {
    // CRITICAL 1: when NOTHING else changed (no lower/top op above), and the
    // family record itself already matches, skip the write entirely — a
    // save of an untouched grouped package is [] ops. The moment any real
    // change happened (a create/update above), the family write always
    // follows it, same as before.
    const familyUnchanged = ops.length === 0
      && sameIds(tierIds, family.tierIds)
      && topOf(family) === pkg.id
      && (family.defaultTierId ?? pkg.id) === defaultTierId;
    if (!familyUnchanged) ops.push({ kind: 'updateFamily', id: familyId, body: familyBody });
  } else {
    ops.push({ kind: 'createFamily', body: { ...familyBody, description: '', productType: null, addonIds: [], noticeDays: 30, enrollment: 'manual', status: 'active' } });
  }
  return ops;
}

/**
 * Runs the ops `planTierSave` planned and returns, for any tier CREATED in
 * this run, a `rank -> new package id` map — the only way a caller can learn
 * the real id of a tier that had no id before this save (Plan B Task B6:
 * `planTokenSaveOps` needs this to resolve a just-created tier's token
 * draft, since it never infers an id from position or `tierRank`).
 */
export async function runTierSave(ops: TierSaveOp[], api: {
  packages: { create: (b: any) => Promise<{ package: { id: string } }>; update: (id: string, b: any) => Promise<unknown> };
  packageFamilies: { create: (b: any) => Promise<unknown>; update: (id: string, b: any) => Promise<unknown> };
}): Promise<Record<number, string>> {
  const newIds = new Map<string, string>();
  const createdByRank: Record<number, string> = {};
  for (const op of ops) {
    if (op.kind === 'createPackage') {
      const r = await api.packages.create(op.body);
      newIds.set(`@new${op.body.tierRank}`, r.package.id);
      if (typeof op.body.tierRank === 'number') createdByRank[op.body.tierRank] = r.package.id;
    } else if (op.kind === 'updatePackage') {
      await api.packages.update(op.id, op.body);
    } else {
      const tierIds = (op.body.tierIds as string[]).map((t) => newIds.get(t) ?? t);
      const body = { ...op.body, tierIds };
      if (op.kind === 'createFamily') await api.packageFamilies.create(body);
      else await api.packageFamilies.update(op.id, body);
    }
  }
  return createdByRank;
}

/** Same rule as shared-types tierLabelOf (kept local: frontend CI builds
 *  against shared-types@development, which has no packageTiers yet). */
function tierLabel(p: { tierLabel?: string; tierRank?: number }): string | undefined {
  if (p.tierLabel && p.tierLabel.trim()) return p.tierLabel.trim();
  if (typeof p.tierRank === 'number' && p.tierRank >= 1) return LABELS[p.tierRank - 1];
  return undefined;
}

/**
 * featureId → owning package name, for the editor's "used by another package"
 * lock. Skips the edited package AND every package of its family (lower tiers
 * carry subsets of the top's features by design), so the Advanced features stay
 * editable once a lower tier exists. No families ⇒ today's map exactly.
 */
export function featureOwnersOutsideFamily(
  pkgs: PackageLike[], editing: PackageLike | null | undefined, families: FamilyLike[],
): Record<string, string> {
  const fam = editing ? familyOf(editing, families) : undefined;
  const famIds = new Set(fam?.tierIds ?? []);
  const famId = fam?.id ?? editing?.familyId;
  return pkgs.reduce((acc, pkg) => {
    if (editing && pkg.id === editing.id) return acc;
    if (famIds.has(pkg.id) || (famId && pkg.familyId === famId)) return acc;
    (pkg.features || []).forEach((fId) => { acc[fId] = pkg.name; });
    return acc;
  }, {} as Record<string, string>);
}

/**
 * Spec §3.3 — a tenant's package toggle list shows ONE row per package. A
 * tenant holding a lower tier sees that tier's row (toggling THAT id),
 * labelled "Name · <tier label>"; a family it does not hold shows the top id.
 * No families ⇒ the input list, same objects, same order.
 */
export function tenantPackageRows<T extends { id: string; name: string; tierLabel?: string; tierRank?: number }>(all: T[], families: FamilyLike[], held: readonly string[]): T[] {
  if (!families.length) return all;
  // Matched by family.tierIds, not row fields: getTenantPackages rows may not
  // carry familyId/role. A lower tier row is folded into its top's slot when
  // the top is in the list; otherwise it stays as its own row (as today).
  const famOf = (id: string) => families.find((x) => Array.isArray(x.tierIds) && x.tierIds.includes(id));
  const inList = (id: string | undefined) => !!id && all.some((x) => x.id === id);
  return all.filter((p) => {
    const f = famOf(p.id);
    return !f || topOf(f) === p.id || !inList(topOf(f));
  }).map((p) => {
    if (held.includes(p.id)) return p;
    const f = famOf(p.id);
    if (!f || topOf(f) !== p.id) return p;
    const heldIds = f.tierIds.filter((t) => t !== p.id && held.includes(t));
    const row = heldIds.length ? all.find((x) => x.id === heldIds[heldIds.length - 1]) : undefined;
    if (!row) return p;
    const label = tierLabel(row);
    return { ...row, name: label ? `${p.name} · ${label}` : p.name };
  });
}
